import { getConfig } from '@sydle/ui-utils';
import { ACCESSIBILITY_BAR_HEIGHT } from '../constants';
import { SDColorHelper } from './color.utils';

export function setTheme(light?: boolean): void {
  if (light && document.body.classList.contains('sy-high-contrast-theme')) {
    document.body.classList.remove('sy-high-contrast-theme');
  } else if (!document.body.classList.contains('sy-high-contrast-theme')) {
    document.body.classList.add('sy-high-contrast-theme');
  }
}

export function isHighContrastTheme(): boolean {
  return document.body.classList.contains('sy-high-contrast-theme');
}

export function setGlobalTokens(): void {
  const primaryColor = getConfig<any>('ui-design-system')?.primaryColor;
  const accentColor = getConfig<any>('ui-design-system')?.secondaryColor;

  let style: { [key: string]: string } = {
    '--sy-sd-high-contrast-background-hover': '#555',
    '--sy-sd-link-color': 'var(--sy-info)',
    '--sy-sd-text-foreground-low-emphasis': 'var(--sy-foreground-low-emphasis)',
    '--sy-sd-text-link-foreground-high-emphasis': 'var(--sy-foreground-high-emphasis)',
    '--sy-sd-surface-elevated-hover': 'var(--sy-surface-elevated-hover)',
    '--sy-sd-high-contrast-surface-elevated-hover': 'var(--sy-sd-high-contrast-background-hover)',
    '--sy-sd-high-contrast-text-foreground-low-emphasis': '#ff0',
    '--sy-sd-white-background-color': '#fff',
    '--sy-sd-high-contrast-background-color': '#000',
    '--sy-sd-high-contrast-link-color': '#ff0',
    '--sy-sd-text-color': '#000',
    '--sy-sd-high-contrast-text-color': '#ff0',
    '--sy-sd-link-color-hover': 'var(--sy-info)',
    '--sy-sd-high-contrast-link-color-hover': '#ff0',
    '--sy-sd-link-text-decoration': 'none',
    '--sy-sd-high-contrast-link-text-decoration': 'underline #ff0',
    '--sy-sd-link-text-decoration-hover': 'none',
    '--sy-sd-high-contrast-link-text-decoration-hover': 'underline #ff0',
    '--sy-sd-menu-background-color': 'none',
    '--sy-sd-menu-high-contrast-background-color': 'var(--sy-sd-high-contrast-background-hover)',
    '--sy-sd-outline-focus-visible': '2px solid var(--sy-focus-visible-color)',
    '--sy-sd-high-contrast-outline-focus-visible': '1px dashed var(--sy-sd-high-contrast-background-hover)',
    '--sy-sd-button-color-basic': 'transparent',
    '--sy-sd-button-high-contrast-color-basic': 'var(--sy-sd-high-contrast-background-hover)',
    '--sy-sd-sidenav-surface-elevated': '#fff',
    '--sy-sd-accessibility-bar-height': `${ACCESSIBILITY_BAR_HEIGHT}px`,
  };

  let elementsColor;

  if (SDColorHelper.getContrastColor(accentColor) === '#FFFFFF') {
    elementsColor = accentColor;
  } else if (SDColorHelper.getContrastColor(primaryColor) === '#FFFFFF') {
    elementsColor = primaryColor;
  } else {
    elementsColor = '#000';
  }

  style = { ...style, '--sy-sd-elements': elementsColor };
  const variants = SDColorHelper.getColorVariations(elementsColor);
  (variants || []).forEach((variant: { name: string; value: string }) => {
    style = { ...style, [`--sy-sd-elements-${variant.name}`]: variant.value };
  });

  setElementTokens(document.body, style, 'body', ':root');
}

/**
 * Create a style tag inside of a HTML element setting tokens for a specific tag.
 */
export function setElementTokens(
  element: HTMLElement,
  tokens: { [key: string]: string },
  tag: string,
  selector?: string,
): void {
  const identifier = `sy-sd-style-${tag.toLowerCase()}`;
  const styleElement = document.createElement('style');

  styleElement.innerHTML = `${selector ?? tag} ${JSON.stringify(tokens)?.replace(/"/g, '').replace(/,/g, ';')}`;
  styleElement.id = identifier;

  element?.querySelector(`#${identifier}`)?.remove();
  element?.appendChild(styleElement);
}
